import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, X, Phone, Mail, User, Calendar, Flag, Tag, AlignLeft, FileText, Building2, Link2, Clock, CheckCircle2, Circle, AlertCircle } from 'lucide-react';
import { tasksAPI, employeesAPI, contactsAPI, companiesAPI } from '../../../services/api';
import { toast } from 'react-toastify';

/* ─── tiny helpers ─── */
const fmt = (val) => val ? new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const PRIORITY_CONFIG = {
  HIGH:   { color: 'text-red-600 bg-red-50 border-red-200',    dot: 'bg-red-500' },
  MEDIUM: { color: 'text-amber-600 bg-amber-50 border-amber-200', dot: 'bg-amber-500' },
  LOW:    { color: 'text-green-600 bg-green-50 border-green-200', dot: 'bg-green-500' },
};

const STATUS_CONFIG = {
  PENDING:     { color: 'text-gray-600 bg-gray-100 border-gray-200',   icon: Circle },
  IN_PROGRESS: { color: 'text-blue-600 bg-blue-50 border-blue-200',    icon: AlertCircle },
  COMPLETED:   { color: 'text-green-600 bg-green-50 border-green-200', icon: CheckCircle2 },
  CANCELLED:   { color: 'text-red-600 bg-red-50 border-red-200',       icon: X },
};

const TYPE_ICONS = { CALL: Phone, EMAIL: Mail, MEETING: Calendar, FOLLOW_UP: Link2, DEMO: FileText, OTHER: Tag };

const Badge = ({ value, config }) => {
  const cfg = config[value] || {};
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.color || 'text-gray-600 bg-gray-100 border-gray-200'}`}>
      {cfg.dot && <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />}
      {Icon && <Icon size={12} />}
      {value?.replace('_', ' ')}
    </span>
  );
};

/* ─── main component ─── */
const TaskPreviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeeNameCache, setEmployeeNameCache] = useState({});
  const [contactNameCache, setContactNameCache] = useState({});
  const [companyNameCache, setCompanyNameCache] = useState({});
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => { fetchTask(); fetchEmployeeOptions(); }, [id]);

  useEffect(() => {
    if (!task?.assignedTo) return;
    const empId = task.assignedTo;
    if (empId in employeeNameCache) return;
    employeesAPI.getEmployeeById(empId)
      .then(res => { const e = res.data; setEmployeeNameCache(p => ({ ...p, [empId]: e.name || `${e.firstName||''} ${e.lastName||''}`.trim() || e.email || empId })); })
      .catch(() => setEmployeeNameCache(p => ({ ...p, [empId]: empId })));
  }, [task?.assignedTo]);

  useEffect(() => {
    if (!task?.notesLastEditedBy) return;
    const editorId = task.notesLastEditedBy;
    if (editorId in employeeNameCache) return;
    employeesAPI.getEmployeeById(editorId)
      .then(res => { const e = res.data; setEmployeeNameCache(p => ({ ...p, [editorId]: e.name || `${e.firstName||''} ${e.lastName||''}`.trim() || e.email || editorId })); })
      .catch(() => setEmployeeNameCache(p => ({ ...p, [editorId]: editorId })));
  }, [task?.notesLastEditedBy]);

  useEffect(() => {
    if (!task?.linkedContact) return;
    const cId = task.linkedContact;
    if (cId in contactNameCache) return;
    contactsAPI.getContactById(cId)
      .then(res => { const c = res.data; setContactNameCache(p => ({ ...p, [cId]: c.name || `${c.firstName||''} ${c.lastName||''}`.trim() || c.email || cId })); })
      .catch(() => setContactNameCache(p => ({ ...p, [cId]: cId })));
  }, [task?.linkedContact]);

  useEffect(() => {
    if (!task?.linkedAccount) return;
    const coId = task.linkedAccount;
    if (coId in companyNameCache) return;
    companiesAPI.getCompanyById(coId)
      .then(res => { const c = res.data; setCompanyNameCache(p => ({ ...p, [coId]: c.companyName || c.name || coId })); })
      .catch(() => setCompanyNameCache(p => ({ ...p, [coId]: coId })));
  }, [task?.linkedAccount]);

  const fetchTask = async () => {
    setLoading(true);
    try {
      let foundTask = null;
      try { const res = await tasksAPI.getTaskById(id); foundTask = res.data; }
      catch { const r = await tasksAPI.getAllTasks(); foundTask = r.data.find(t => t.id === id); }
      if (foundTask) { setTask(foundTask); setEditValues(foundTask); }
      else { toast.error('Task not found'); navigate('/tasks'); }
    } catch { toast.error('Failed to fetch task details'); navigate('/tasks'); }
    finally { setLoading(false); }
  };

  const fetchEmployeeOptions = async () => {
    try {
      const r = await employeesAPI.getAllEmployees();
      setEmployeeOptions(r.data.filter(e => Array.isArray(e.roles) && e.roles.some(r => r === 'CRM' || r === 'ROLE_CRM')));
    } catch (e) { console.error(e); }
  };

  const handleFieldEdit = (key) => {
    if (['subject', 'linkedContact', 'linkedAccount', 'createdAt'].includes(key)) {
      toast.info('This field cannot be edited from preview'); return;
    }
    setEditingField(key);
  };

  const handleSave = async (key) => {
    try {
      const newVal = editValues[key];
      if ((newVal === '' || newVal == null) && task[key]) {
        toast.warning(`Cannot clear "${key}" — please enter a valid value`);
        handleCancel(); return;
      }
      await tasksAPI.updateTask(id, { [key]: newVal ?? null });
      setTask(p => ({ ...p, [key]: newVal }));
      setEditValues(p => ({ ...p, [key]: newVal }));
      setEditingField(null);
      toast.success(`${key} updated successfully`);
    } catch (err) {
      toast.error('Failed to update: ' + (err.response?.data?.message || err.message));
      setEditValues(task); setEditingField(null);
    }
  };

  const handleCancel = () => { setEditingField(null); setEditValues(task); };

  /* ── field definitions ── */
  const fields = [
    { key: 'type',      label: 'Type',       type: 'select', icon: Tag,
      options: ['CALL','EMAIL','MEETING','FOLLOW_UP','DEMO','OTHER'].map(v => ({ id: v, name: v.replace('_',' ') })) },
    { key: 'dueDate',   label: 'Due Date',   type: 'date',   icon: Calendar },
    { key: 'priority',  label: 'Priority',   type: 'select', icon: Flag,
      options: ['HIGH','MEDIUM','LOW'].map(v => ({ id: v, name: v })) },
    { key: 'assignedTo',label: 'Assigned To',type: 'select', icon: User,   options: employeeOptions },
    { key: 'status',    label: 'Status',     type: 'select', icon: CheckCircle2,
      options: [{ id:'PENDING',name:'Pending'},{ id:'IN_PROGRESS',name:'In Progress'},{ id:'COMPLETED',name:'Completed'},{ id:'CANCELLED',name:'Cancelled'}] },
    { key: 'linkedContact', label: 'Linked Contact', type: 'readonly', icon: User },
    { key: 'linkedAccount', label: 'Linked Account', type: 'readonly', icon: Building2 },
    { key: 'createdAt', label: 'Created At', type: 'readonly', icon: Clock },
    { key: 'description',label:'Description',type: 'textarea', icon: AlignLeft, wide: true },
    { key: 'notes',     label: 'Notes',      type: 'textarea', icon: FileText, wide: true },
  ];

  const getDisplay = (f) => {
    const v = editValues[f.key];
    if (f.key === 'assignedTo') return employeeNameCache[v] || v || '—';
    if (f.key === 'linkedContact') return contactNameCache[v] || v || '—';
    if (f.key === 'linkedAccount') return companyNameCache[v] || v || '—';
    if (f.key.toLowerCase().includes('date') || f.key === 'createdAt') return fmt(v);
    if (f.options) { const m = f.options.find(o => o.id === v); return m ? m.name : (v || '—'); }
    return v || '—';
  };

  const TypeIcon = task ? (TYPE_ICONS[task.type] || Tag) : Tag;

  if (loading) return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Loading task…</p>
      </div>
    </div>
  );

  if (!task) return <div className="p-6 text-center text-red-600">Task not found</div>;

  return (
    <div className="min-h-screen bg-[#f5f8fa] font-sans">
      {/* Top nav bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-3 sticky top-0 z-20 shadow-sm">
        <button onClick={() => navigate('/tasks')} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors">
          <ChevronLeft size={16} /> Tasks
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-sm font-medium text-gray-800 truncate max-w-xs">{task.subject || '(No Subject)'}</span>
      </div>

      <div className="flex h-[calc(100vh-49px)]">
        {/* ── LEFT SIDEBAR ── */}
        <aside className="w-72 bg-white border-r border-gray-200 flex-shrink-0 overflow-y-auto">
          {/* Task identity */}
          <div className="p-5 border-b border-gray-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                <TypeIcon size={18} className="text-orange-600" />
              </div>
              <div className="min-w-0">
                <h2 className="font-semibold text-gray-900 text-sm leading-tight truncate">{task.subject || '(No Subject)'}</h2>
                <p className="text-xs text-gray-400 mt-0.5">Task</p>
              </div>
            </div>

            {/* Quick badges */}
            <div className="flex flex-wrap gap-2 mt-3">
              {task.status && <Badge value={task.status} config={STATUS_CONFIG} />}
              {task.priority && <Badge value={task.priority} config={PRIORITY_CONFIG} />}
            </div>
          </div>

          {/* Sidebar info rows */}
          <div className="p-4">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">About this task</p>
            {[
              { label: 'Type',        value: task.type?.replace('_',' ') || '—', icon: Tag },
              { label: 'Due Date',    value: fmt(task.dueDate), icon: Calendar },
              { label: 'Assigned To', value: employeeNameCache[task.assignedTo] || task.assignedTo || '—', icon: User },
              { label: 'Contact',     value: contactNameCache[task.linkedContact] || task.linkedContact || '—', icon: User },
              { label: 'Account',     value: companyNameCache[task.linkedAccount] || task.linkedAccount || '—', icon: Building2 },
              { label: 'Created',     value: fmt(task.createdAt), icon: Clock },
            ].map(({ label, value, icon: Icon }) => (
              <div key={label} className="flex items-start gap-2.5 py-2 border-b border-gray-50 last:border-0">
                <Icon size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-400">{label}</p>
                  <p className="text-xs text-gray-800 font-medium truncate">{value}</p>
                </div>
              </div>
            ))}
          </div>
        </aside>

        {/* ── CENTER MAIN ── */}
        <main className="flex-1 overflow-y-auto">
          {/* Tabs */}
          <div className="bg-white border-b border-gray-200 px-6 sticky top-0 z-10">
            <nav className="flex gap-0">
              {['details', 'notes', 'activity'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3.5 text-sm font-medium capitalize border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-orange-500 text-orange-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'details' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {fields.filter(f => !['description','notes'].includes(f.key)).map(field => (
                  <EditableField
                    key={field.key}
                    field={field}
                    editingField={editingField}
                    editValues={editValues}
                    employeeOptions={employeeOptions}
                    display={getDisplay(field)}
                    onEdit={() => handleFieldEdit(field.key)}
                    onSave={() => handleSave(field.key)}
                    onCancel={handleCancel}
                    onChange={(e) => setEditValues(p => ({ ...p, [field.key]: e.target.value }))}
                  />
                ))}

                {/* Description full-width */}
                {fields.filter(f => f.key === 'description').map(field => (
                  <div key={field.key} className="md:col-span-2">
                    <EditableField
                      field={field}
                      editingField={editingField}
                      editValues={editValues}
                      employeeOptions={employeeOptions}
                      display={getDisplay(field)}
                      onEdit={() => handleFieldEdit(field.key)}
                      onSave={() => handleSave(field.key)}
                      onCancel={handleCancel}
                      onChange={(e) => setEditValues(p => ({ ...p, [field.key]: e.target.value }))}
                    />
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="max-w-2xl">
                {task?.notesLastEditedAt && (
                  <p className="text-xs text-gray-400 mb-2">
                    Last edited by {employeeNameCache[task.notesLastEditedBy] || task.notesLastEditedBy} on {new Date(task.notesLastEditedAt).toLocaleString()}
                  </p>
                )}
                {fields.filter(f => f.key === 'notes').map(field => (
                  <EditableField
                    key={field.key}
                    field={field}
                    editingField={editingField}
                    editValues={editValues}
                    employeeOptions={employeeOptions}
                    display={getDisplay(field)}
                    onEdit={() => handleFieldEdit(field.key)}
                    onSave={() => handleSave(field.key)}
                    onCancel={handleCancel}
                    onChange={(e) => setEditValues(p => ({ ...p, [field.key]: e.target.value }))}
                  />
                ))}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="max-w-2xl">
                <ActivityTimeline task={task} employeeNameCache={employeeNameCache} />
              </div>
            )}
          </div>
        </main>

        {/* ── RIGHT PANEL ── */}
        <aside className="w-64 bg-white border-l border-gray-200 flex-shrink-0 overflow-y-auto">
          <div className="p-4 border-b border-gray-100">
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-3">Quick Info</p>

            <InfoCard title="Status" icon={CheckCircle2}>
              <Badge value={task.status} config={STATUS_CONFIG} />
            </InfoCard>

            <InfoCard title="Priority" icon={Flag}>
              <Badge value={task.priority} config={PRIORITY_CONFIG} />
            </InfoCard>

            <InfoCard title="Due Date" icon={Calendar}>
              <span className="text-sm text-gray-700">{fmt(task.dueDate)}</span>
            </InfoCard>

            <InfoCard title="Assigned To" icon={User}>
              {task.assignedTo ? (
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center">
                    <User size={12} className="text-orange-600" />
                  </div>
                  <span className="text-sm text-gray-700 truncate">{employeeNameCache[task.assignedTo] || task.assignedTo}</span>
                </div>
              ) : <span className="text-sm text-gray-400">Unassigned</span>}
            </InfoCard>
          </div>

          <div className="p-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Linked Contact</p>
            </div>
            {task.linkedContact ? (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <User size={13} className="text-blue-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 font-medium truncate">{contactNameCache[task.linkedContact] || '—'}</p>
                </div>
              </div>
            ) : <p className="text-xs text-gray-400">No linked contact</p>}
          </div>

          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Linked Account</p>
            </div>
            {task.linkedAccount ? (
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                  <Building2 size={13} className="text-purple-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm text-gray-800 font-medium truncate">{companyNameCache[task.linkedAccount] || '—'}</p>
                </div>
              </div>
            ) : <p className="text-xs text-gray-400">No linked account</p>}
          </div>
        </aside>
      </div>
    </div>
  );
};

/* ─── sub-components ─── */

const InfoCard = ({ title, icon: Icon, children }) => (
  <div className="mb-4 last:mb-0">
    <div className="flex items-center gap-1.5 mb-1.5">
      <Icon size={12} className="text-gray-400" />
      <p className="text-[11px] text-gray-400">{title}</p>
    </div>
    {children}
  </div>
);

const EditableField = ({ field, editingField, editValues, employeeOptions, display, onEdit, onSave, onCancel, onChange }) => {
  const isEditing = editingField === field.key;
  const isReadonly = field.type === 'readonly';
  const Icon = field.icon;

  return (
    <div className={`bg-white rounded-lg border border-gray-200 p-4 ${field.wide ? 'col-span-full' : ''}`}>
      <div className="flex items-center gap-1.5 mb-2">
        {Icon && <Icon size={13} className="text-gray-400" />}
        <label className="text-xs font-medium text-gray-500">{field.label}</label>
      </div>

      {isEditing ? (
        <div className="space-y-2">
          {field.type === 'select' ? (
            <select
              value={editValues[field.key] || ''}
              onChange={onChange}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            >
              <option value="">Select…</option>
              {field.options.map(o => (
                <option key={o.id} value={o.id}>
                  {o.name || `${o.firstName||''} ${o.lastName||''}`.trim() || o.email}
                </option>
              ))}
            </select>
          ) : field.type === 'textarea' ? (
            <textarea
              value={editValues[field.key] || ''}
              onChange={onChange}
              rows={4}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent resize-none"
            />
          ) : (
            <input
              type={field.type === 'date' ? 'date' : 'text'}
              value={field.type === 'date' && editValues[field.key] ? new Date(editValues[field.key]).toISOString().slice(0,10) : editValues[field.key] || ''}
              onChange={onChange}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            />
          )}
          <div className="flex gap-2 justify-end">
            <button onClick={onCancel} className="flex items-center gap-1 px-3 py-1.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
              <X size={12} /> Cancel
            </button>
            <button onClick={onSave} className="flex items-center gap-1 px-3 py-1.5 text-xs text-white bg-orange-500 hover:bg-orange-600 rounded-md transition-colors">
              <Save size={12} /> Save
            </button>
          </div>
        </div>
      ) : (
        <div
          onClick={!isReadonly ? onEdit : undefined}
          className={`text-sm text-gray-800 rounded-md px-2 py-1.5 -mx-2 ${field.type === 'textarea' ? 'min-h-16 whitespace-pre-wrap' : ''}
            ${!isReadonly ? 'cursor-pointer hover:bg-orange-50 hover:text-orange-700 transition-colors group' : 'cursor-default'}
          `}
          title={!isReadonly ? 'Click to edit' : undefined}
        >
          {display === '—' ? <span className="text-gray-400 italic text-xs">Not set{!isReadonly ? ' · click to edit' : ''}</span> : display}
        </div>
      )}
    </div>
  );
};

const ActivityTimeline = ({ task, employeeNameCache }) => {
  const events = [
    { type: 'created', text: 'Task created', date: task.createdAt },
    task.assignedTo && { type: 'assigned', text: `Assigned to ${employeeNameCache[task.assignedTo] || task.assignedTo}`, date: task.createdAt },
    task.dueDate && { type: 'due', text: `Due on ${new Date(task.dueDate).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`, date: task.dueDate },
  ].filter(Boolean);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-4">Activity</h3>
      {events.length === 0 && <p className="text-sm text-gray-400">No activity yet.</p>}
      <div className="relative">
        <div className="absolute left-2 top-0 bottom-0 w-px bg-gray-100" />
        <div className="space-y-4 pl-7">
          {events.map((ev, i) => (
            <div key={i} className="relative">
              <div className="absolute -left-5 w-3 h-3 rounded-full bg-orange-400 border-2 border-white ring-1 ring-orange-200 top-1" />
              <p className="text-sm text-gray-700">{ev.text}</p>
              <p className="text-xs text-gray-400 mt-0.5">{ev.date ? new Date(ev.date).toLocaleDateString() : ''}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TaskPreviewPage;